import { useState, useRef, useEffect } from 'react'
import { 
  Package, 
  Plus, 
  Edit, 
  Trash2, 
  Upload, 
  Save, 
  X, 
  Tag,
  FileText
} from 'lucide-react'
import { useSupabaseEquipmentStore } from '../stores/supabaseEquipmentStore'
import { Equipment } from '../../types'

interface EquipmentFormData {
  name: string
  description: string
  category: string
  price: number
  sizes: string[]
  colors: string[]
  image: string
  inStock: boolean
}

export default function StoreManagerPage() {
  const {
    equipment,
    categories,
    loading,
    error,
    fetchEquipment,
    fetchCategories,
    addEquipment,
    updateEquipment,
    deleteEquipment,
    uploadEquipmentImage,
    addCategory,
    updateCategory,
    deleteCategory,
    getAllCategories
  } = useSupabaseEquipmentStore()

  const [activeTab, setActiveTab] = useState<'equipment' | 'categories'>('equipment')
  const [showEquipmentForm, setShowEquipmentForm] = useState(false)
  const [showCategoryForm, setShowCategoryForm] = useState(false)
  const [editingEquipment, setEditingEquipment] = useState<Equipment | null>(null)
  const [editingCategory, setEditingCategory] = useState<any>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>('Alle')
  const [imageUploading, setImageUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [equipmentForm, setEquipmentForm] = useState<EquipmentFormData>({
    name: '',
    description: '',
    category: '',
    price: 0,
    sizes: [],
    colors: [],
    image: '',
    inStock: true
  })

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    order_index: 0
  })

  // Lade Daten beim Start und bei Tab-Wechsel
  useEffect(() => {
    fetchEquipment()
    fetchCategories()
  }, [])

  // Stelle sicher, dass Kategorien geladen sind
  useEffect(() => {
    if (categories.length === 0) {
      fetchCategories()
    }
  }, [categories.length])

  const allCategories = ['Alle', ...getAllCategories()]
  const filteredEquipment = selectedCategory === 'Alle' 
    ? equipment 
    : equipment.filter(item => item.category === selectedCategory)

  const handleImageUpload = async (file: File) => {
    setImageUploading(true)
    try {
      const result = await uploadEquipmentImage(file)
      if (result.url) {
        setEquipmentForm(prev => ({ ...prev, image: result.url! }))
      } else if (result.error) {
        alert(`Fehler beim Hochladen: ${result.error}`)
      }
    } catch (error) {
      alert('Fehler beim Hochladen des Bildes')
    } finally {
      setImageUploading(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('Datei ist zu groß. Maximal 5MB erlaubt.')
        return
      }
      if (!file.type.startsWith('image/')) {
        alert('Nur Bilder sind erlaubt.')
        return
      }
      handleImageUpload(file)
    }
  }

  const resetEquipmentForm = () => {
    setEquipmentForm({
      name: '',
      description: '',
      category: '',
      price: 0,
      sizes: [],
      colors: [],
      image: '',
      inStock: true
    })
    setEditingEquipment(null)
    setShowEquipmentForm(false)
  }

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      description: '',
      order_index: 0
    })
    setEditingCategory(null)
    setShowCategoryForm(false)
  }

  const handleSubmitEquipment = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!equipmentForm.name || !equipmentForm.category || equipmentForm.price <= 0) {
      alert('Bitte füllen Sie alle Pflichtfelder aus.')
      return
    }

    try {
      const equipmentData = {
        ...equipmentForm,
        sizes: equipmentForm.sizes.length > 0 ? equipmentForm.sizes : ['One Size'],
        colors: equipmentForm.colors.length > 0 ? equipmentForm.colors : ['Standard']
      }

      let result
      if (editingEquipment) {
        result = await updateEquipment(editingEquipment.id, equipmentData)
      } else {
        result = await addEquipment(equipmentData)
      }

      if (result.error) {
        alert(`Fehler: ${result.error}`)
      } else {
        alert(editingEquipment ? 'Equipment aktualisiert!' : 'Equipment hinzugefügt!')
        resetEquipmentForm()
        fetchEquipment() // Refresh data
      }
    } catch (error) {
      alert('Fehler beim Speichern')
    }
  }

  const handleSubmitCategory = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!categoryForm.name) {
      alert('Bitte geben Sie einen Kategorienamen ein.')
      return
    }

    try {
      let result
      if (editingCategory) {
        result = await updateCategory(editingCategory.id, categoryForm)
      } else {
        result = await addCategory(categoryForm)
      }

      if (result.error) {
        alert(`Fehler: ${result.error}`)
      } else {
        alert(editingCategory ? 'Kategorie aktualisiert!' : 'Kategorie hinzugefügt!')
        resetCategoryForm()
        fetchCategories() // Refresh data
      }
    } catch (error) {
      alert('Fehler beim Speichern')
    }
  }

  const handleEditEquipment = (equipment: Equipment) => {
    setEquipmentForm({
      name: equipment.name,
      description: equipment.description,
      category: equipment.category,
      price: equipment.price || 0,
      sizes: equipment.sizes || [],
      colors: equipment.colors || [],
      image: equipment.image || '',
      inStock: equipment.inStock !== false
    })
    setEditingEquipment(equipment)
    setShowEquipmentForm(true)
  }

  const handleEditCategory = (category: any) => {
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      order_index: category.order_index || 0
    })
    setEditingCategory(category)
    setShowCategoryForm(true)
  }

  const handleDeleteEquipment = async (id: string, name: string) => {
    if (confirm(`Möchten Sie "${name}" wirklich löschen?`)) {
      const result = await deleteEquipment(id)
      if (result.error) {
        alert(`Fehler: ${result.error}`)
      } else {
        alert('Equipment gelöscht!')
        fetchEquipment()
      }
    }
  }

  const handleDeleteCategory = async (id: string, name: string) => {
    if (confirm(`Möchten Sie die Kategorie "${name}" wirklich löschen?`)) {
      const result = await deleteCategory(id)
      if (result.error) {
        alert(`Fehler: ${result.error}`)
      } else {
        alert('Kategorie gelöscht!')
        fetchCategories()
      }
    }
  }

  const addSize = (size: string) => {
    if (size && !equipmentForm.sizes.includes(size)) {
      setEquipmentForm(prev => ({
        ...prev,
        sizes: [...prev.sizes, size]
      }))
    }
  }

  const removeSize = (size: string) => {
    setEquipmentForm(prev => ({
      ...prev,
      sizes: prev.sizes.filter(s => s !== size)
    }))
  }

  const addColor = (color: string) => {
    if (color && !equipmentForm.colors.includes(color)) {
      setEquipmentForm(prev => ({
        ...prev,
        colors: [...prev.colors, color]
      }))
    }
  }

  const removeColor = (color: string) => {
    setEquipmentForm(prev => ({
      ...prev,
      colors: prev.colors.filter(c => c !== color)
    }))
  }

  if (loading) {
    return (
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '24px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          padding: '48px',
          textAlign: 'center'
        }}>
          <Package size={64} style={{ 
            margin: '0 auto 16px', 
            color: '#9CA3AF',
            animation: 'pulse 2s infinite'
          }} />
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0'
          }}>Lade Store Manager...</h2>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '24px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          padding: '48px',
          textAlign: 'center'
        }}>
          <Package size={64} style={{ 
            margin: '0 auto 16px', 
            color: '#EF4444'
          }} />
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#DC2626',
            margin: '0 0 8px 0'
          }}>Fehler beim Laden</h2>
          <p style={{
            color: '#6B7280',
            marginBottom: '16px'
          }}>{error}</p>
          <button 
            onClick={() => { fetchEquipment(); fetchCategories(); }}
            style={{
              background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #15803D 0%, #16A34A 100%)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)'
            }}
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '24px',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        padding: '24px',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: '1px solid #e5e7eb'
      }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '800',
          background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '8px',
          letterSpacing: '-0.025em'
        }}>
          Store Manager
        </h1>
        <p style={{
          color: '#6B7280',
          fontSize: '16px',
          fontWeight: '500',
          margin: '0'
        }}>
          Equipment und Kategorien verwalten, Bilder hochladen und Preise bearbeiten.
        </p>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        marginBottom: '24px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: '1px solid #e5e7eb',
        padding: '20px'
      }}>
        <div style={{
          display: 'flex',
          gap: '12px'
        }}>
          <button
            onClick={() => setActiveTab('equipment')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: activeTab === 'equipment' 
                ? 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)' 
                : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
              color: activeTab === 'equipment' ? 'white' : '#6B7280',
              border: activeTab === 'equipment' ? 'none' : '1px solid #d1d5db',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              boxShadow: activeTab === 'equipment' 
                ? '0 4px 6px -1px rgba(27, 126, 58, 0.3)' 
                : '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'equipment') {
                e.currentTarget.style.background = 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)'
                e.currentTarget.style.color = '#374151'
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'equipment') {
                e.currentTarget.style.background = 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)'
                e.currentTarget.style.color = '#6B7280'
              }
            }}
          >
            <Package size={16} />
            Equipment ({equipment.length})
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: activeTab === 'categories' 
                ? 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)' 
                : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
              color: activeTab === 'categories' ? 'white' : '#6B7280',
              border: activeTab === 'categories' ? 'none' : '1px solid #d1d5db',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              boxShadow: activeTab === 'categories' 
                ? '0 4px 6px -1px rgba(27, 126, 58, 0.3)' 
                : '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'categories') {
                e.currentTarget.style.background = 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)'
                e.currentTarget.style.color = '#374151'
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'categories') {
                e.currentTarget.style.background = 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)'
                e.currentTarget.style.color = '#6B7280'
              }
            }}
          >
            <Tag size={16} />
            Kategorien ({categories.length})
          </button>
        </div>
      </div>

      {/* Equipment Tab */}
      {activeTab === 'equipment' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Filters und Add Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            padding: '20px',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb'
          }}>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '500',
                color: '#374151',
                background: 'white',
                cursor: 'pointer',
                minWidth: '200px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
            >
              {allCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <button
              onClick={() => setShowEquipmentForm(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 6px -1px rgba(27, 126, 58, 0.3)',
                transform: 'translateY(0)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #15803D 0%, #16A34A 100%)'
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 8px 25px -5px rgba(27, 126, 58, 0.4)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)'
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
              }}
            >
              <Plus size={16} />
              Neues Equipment
            </button>
          </div>

          {/* Equipment Form */}
          {showEquipmentForm && (
            <div style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: '16px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e5e7eb',
              padding: '24px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#1F2937',
                  margin: '0'
                }}>
                  {editingEquipment ? 'Equipment bearbeiten' : 'Neues Equipment hinzufügen'}
                </h3>
                <button
                  onClick={resetEquipmentForm}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: '#6B7280',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
                    e.currentTarget.style.color = '#DC2626'
                    e.currentTarget.style.borderColor = '#f87171'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)'
                    e.currentTarget.style.color = '#6B7280'
                    e.currentTarget.style.borderColor = '#d1d5db'
                  }}
                >
                  <X size={14} />
                </button>
              </div>

              <form onSubmit={handleSubmitEquipment} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '20px'
                }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#1e293b',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Package size={18} style={{ color: '#1B7E3A' }} />
                      Grundinformationen
                    </h4>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '8px'
                        }}>Name *</label>
                        <input
                          type="text"
                          value={equipmentForm.name}
                          onChange={(e) => setEquipmentForm(prev => ({ ...prev, name: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '1px solid #d1d5db',
                            borderRadius: '12px',
                            fontSize: '16px',
                            backgroundColor: 'white',
                            transition: 'all 0.2s ease',
                            outline: 'none',
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#22C55E'
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = '#d1d5db'
                            e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                          }}
                          placeholder="z.B. Fußball Größe 5"
                          required
                        />
                      </div>

                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '8px'
                        }}>Kategorie *</label>
                        <select
                          value={equipmentForm.category}
                          onChange={(e) => setEquipmentForm(prev => ({ ...prev, category: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '1px solid #d1d5db',
                            borderRadius: '12px',
                            fontSize: '16px',
                            backgroundColor: 'white',
                            cursor: 'pointer',
                            outline: 'none',
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                            transition: 'all 0.2s ease'
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#22C55E'
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = '#d1d5db'
                            e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                          }}
                          required
                        >
                          <option value="">Kategorie wählen</option>
                          {getAllCategories().map(category => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#374151',
                            marginBottom: '8px'
                          }}>Preis (CHF) *</label>
                          <input
                            type="number"
                            step="0.01"
                            value={equipmentForm.price}
                            onChange={(e) => setEquipmentForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: '1px solid #d1d5db',
                              borderRadius: '12px',
                              fontSize: '16px',
                              backgroundColor: 'white',
                              outline: 'none',
                              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                              transition: 'all 0.2s ease'
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#22C55E'
                              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = '#d1d5db'
                              e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                            }}
                            placeholder="0.00"
                            required
                          />
                        </div>

                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#374151',
                            marginBottom: '8px'
                          }}>Verfügbarkeit *</label>
                          <select
                            value={equipmentForm.inStock ? 'true' : 'false'}
                            onChange={(e) => setEquipmentForm(prev => ({ ...prev, inStock: e.target.value === 'true' }))}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: '1px solid #d1d5db',
                              borderRadius: '12px',
                              fontSize: '16px',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              outline: 'none',
                              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                              transition: 'all 0.2s ease'
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#22C55E'
                              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = '#d1d5db'
                              e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                            }}
                          >
                            <option value="true">✅ Verfügbar</option>
                            <option value="false">❌ Nicht verfügbar</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#1e293b',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <FileText size={18} style={{ color: '#1B7E3A' }} />
                      Beschreibung
                    </h4>
                    
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '8px'
                      }}>Produktbeschreibung</label>
                      <textarea
                        value={equipmentForm.description}
                        onChange={(e) => setEquipmentForm(prev => ({ ...prev, description: e.target.value }))}
                        rows={6}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '1px solid #d1d5db',
                          borderRadius: '12px',
                          fontSize: '16px',
                          backgroundColor: 'white',
                          resize: 'vertical',
                          outline: 'none',
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                          transition: 'all 0.2s ease',
                          fontFamily: 'inherit'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = '#22C55E'
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = '#d1d5db'
                          e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                        }}
                        placeholder="Detaillierte Beschreibung des Equipments..."
                      />
                    </div>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#1e293b',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Upload size={18} style={{ color: '#1B7E3A' }} />
                      Medien & Eigenschaften
                    </h4>

                    {/* Image Upload */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '8px'
                        }}>Bild</label>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '16px'
                        }}>
                          {equipmentForm.image && (
                            <img 
                              src={equipmentForm.image} 
                              alt="Equipment" 
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb'
                              }}
                            />
                          )}
                          <div>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleFileSelect}
                              style={{ display: 'none' }}
                            />
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={imageUploading}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '12px 16px',
                                background: imageUploading ? '#9ca3af' : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                                border: '1px solid #d1d5db',
                                borderRadius: '12px',
                                cursor: imageUploading ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#374151',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <Upload size={16} />
                              {imageUploading ? 'Uploading...' : 'Bild hochladen'}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Sizes */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '8px'
                        }}>Größen</label>
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          marginBottom: '12px',
                          flexWrap: 'wrap'
                        }}>
                          {equipmentForm.sizes.map(size => (
                            <span 
                              key={size} 
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)',
                                color: '#374151',
                                fontSize: '12px',
                                fontWeight: '600',
                                padding: '6px 10px',
                                borderRadius: '8px',
                                border: '1px solid #9ca3af'
                              }}
                            >
                              {size}
                              <button 
                                type="button"
                                onClick={() => removeSize(size)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#ef4444',
                                  cursor: 'pointer',
                                  padding: '0',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <X size={12} />
                              </button>
                            </span>
                          ))}
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          flexWrap: 'wrap'
                        }}>
                          {['XS', 'S', 'M', 'L', 'XL', 'XXL'].map(size => (
                            <button
                              key={size}
                              type="button"
                              onClick={() => addSize(size)}
                              disabled={equipmentForm.sizes.includes(size)}
                              style={{
                                padding: '8px 12px',
                                background: equipmentForm.sizes.includes(size) 
                                  ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                                  : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                cursor: equipmentForm.sizes.includes(size) ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                fontWeight: '600',
                                color: equipmentForm.sizes.includes(size) ? '#ffffff' : '#374151',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {size}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Colors */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '8px'
                        }}>Farben</label>
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          marginBottom: '12px',
                          flexWrap: 'wrap'
                        }}>
                          {equipmentForm.colors.map(color => (
                            <span 
                              key={color} 
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)',
                                color: '#374151',
                                fontSize: '12px',
                                fontWeight: '600',
                                padding: '6px 10px',
                                borderRadius: '8px',
                                border: '1px solid #9ca3af'
                              }}
                            >
                              {color}
                              <button 
                                type="button"
                                onClick={() => removeColor(color)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#ef4444',
                                  cursor: 'pointer',
                                  padding: '0',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <X size={12} />
                              </button>
                            </span>
                          ))}
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          flexWrap: 'wrap'
                        }}>
                          {['Schwarz', 'Weiß', 'Rot', 'Blau', 'Grün', 'Gelb'].map(color => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => addColor(color)}
                              disabled={equipmentForm.colors.includes(color)}
                              style={{
                                padding: '8px 12px',
                                background: equipmentForm.colors.includes(color) 
                                  ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                                  : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                cursor: equipmentForm.colors.includes(color) ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                fontWeight: '600',
                                color: equipmentForm.colors.includes(color) ? '#ffffff' : '#374151',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {color}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '12px',
                  paddingTop: '20px'
                }}>
                  <button
                    type="button"
                    onClick={resetEquipmentForm}
                    style={{
                      flex: '1',
                      padding: '14px 20px',
                      background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: '1',
                      padding: '14px 20px',
                      background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: 'white',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
                    }}
                  >
                    <Save size={16} />
                    {editingEquipment ? 'Aktualisieren' : 'Hinzufügen'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Equipment Liste */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
          }}>
            {filteredEquipment.map(equipment => (
              <div key={equipment.id} style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                borderRadius: '16px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)'
                e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}
              >
                {/* Stock Status Indicator */}
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  zIndex: '10',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: 'white',
                  background: equipment.inStock !== false 
                    ? 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)' 
                    : 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                  boxShadow: equipment.inStock !== false 
                    ? '0 2px 8px rgba(22, 163, 74, 0.3)' 
                    : '0 2px 8px rgba(220, 38, 38, 0.3)'
                }}>
                  {equipment.inStock !== false ? '✅ Verfügbar' : '❌ Nicht verfügbar'}
                </div>

                {equipment.image && (
                  <img 
                    src={equipment.image} 
                    alt={equipment.name}
                    style={{
                      width: '100%',
                      height: '192px',
                      objectFit: 'cover',
                      borderTopLeftRadius: '16px',
                      borderTopRightRadius: '16px'
                    }}
                  />
                )}
                <div style={{ padding: '20px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px'
                  }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#1F2937',
                      margin: '0',
                      lineHeight: '1.2',
                      flex: '1',
                      paddingRight: '12px'
                    }}>{equipment.name}</h3>
                  </div>
                  
                  <p style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    marginBottom: '12px',
                    lineHeight: '1.4',
                    display: '-webkit-box',
                    WebkitLineClamp: '2',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>{equipment.description}</p>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}>
                    <span style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#1F2937'
                    }}>CHF {equipment.price?.toFixed(2)}</span>
                    <span style={{
                      padding: '4px 12px',
                      background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                      color: '#374151',
                      fontSize: '12px',
                      fontWeight: '600',
                      borderRadius: '12px',
                      border: '1px solid #d1d5db'
                    }}>{equipment.category}</span>
                  </div>

                  {equipment.sizes && equipment.sizes.length > 0 && (
                    <div style={{ marginBottom: '8px' }}>
                      <small style={{
                        color: '#6B7280',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>Größen: {equipment.sizes.join(', ')}</small>
                    </div>
                  )}

                  {equipment.colors && equipment.colors.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <small style={{
                        color: '#6B7280',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>Farben: {equipment.colors.join(', ')}</small>
                    </div>
                  )}

                  <div style={{
                    display: 'flex',
                    gap: '8px'
                  }}>
                    <button
                      onClick={() => handleEditEquipment(equipment)}
                      style={{
                        flex: '1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '10px 12px',
                        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                        border: '1px solid #d1d5db',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)'
                        e.currentTarget.style.color = 'white'
                        e.currentTarget.style.borderColor = '#22C55E'
                        e.currentTarget.style.transform = 'translateY(-1px)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)'
                        e.currentTarget.style.color = '#374151'
                        e.currentTarget.style.borderColor = '#d1d5db'
                        e.currentTarget.style.transform = 'translateY(0)'
                      }}
                    >
                      <Edit size={14} />
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => handleDeleteEquipment(equipment.id, equipment.name)}
                      style={{
                        flex: '1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '10px 12px',
                        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                        border: '1px solid #d1d5db',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #DC2626 0%, #EF4444 100%)'
                        e.currentTarget.style.color = 'white'
                        e.currentTarget.style.borderColor = '#EF4444'
                        e.currentTarget.style.transform = 'translateY(-1px)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)'
                        e.currentTarget.style.color = '#374151'
                        e.currentTarget.style.borderColor = '#d1d5db'
                        e.currentTarget.style.transform = 'translateY(0)'
                      }}
                    >
                      <Trash2 size={14} />
                      Löschen
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredEquipment.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '64px 32px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: '16px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e5e7eb'
            }}>
              <Package size={64} style={{ 
                margin: '0 auto 20px', 
                color: '#d1d5db' 
              }} />
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1F2937',
                marginBottom: '8px'
              }}>Kein Equipment gefunden</h3>
              <p style={{
                color: '#6B7280',
                fontSize: '16px',
                margin: '0'
              }}>Fügen Sie das erste Equipment-Item hinzu.</p>
            </div>
          )}
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1F2937'
            }}>Kategorien verwalten</h2>
            <button
              onClick={() => setShowCategoryForm(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
              }}
            >
              <Plus size={16} />
              Neue Kategorie
            </button>
          </div>

          {/* Category Form */}
          {showCategoryForm && (
            <div style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: '16px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e5e7eb',
              padding: '24px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#1F2937',
                  margin: '0'
                }}>
                  {editingCategory ? 'Kategorie bearbeiten' : 'Neue Kategorie hinzufügen'}
                </h3>
                <button
                  onClick={resetCategoryForm}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: '#6B7280',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <X size={14} />
                </button>
              </div>

              <form onSubmit={handleSubmitCategory} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>Name *</label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      fontSize: '16px',
                      backgroundColor: 'white',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                    }}
                    required
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>Beschreibung</label>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      fontSize: '16px',
                      backgroundColor: 'white',
                      resize: 'vertical',
                      outline: 'none',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s ease',
                      fontFamily: 'inherit'
                    }}
                    rows={3}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>Reihenfolge</label>
                  <input
                    type="number"
                    value={categoryForm.order_index}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, order_index: parseInt(e.target.value) || 0 }))}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      fontSize: '16px',
                      backgroundColor: 'white',
                      outline: 'none',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s ease'
                    }}
                  />
                </div>

                <div style={{
                  display: 'flex',
                  gap: '12px'
                }}>
                  <button
                    type="button"
                    onClick={resetCategoryForm}
                    style={{
                      flex: '1',
                      padding: '14px 20px',
                      background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: '1',
                      padding: '14px 20px',
                      background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: 'white',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
                    }}
                  >
                    <Save size={16} />
                    {editingCategory ? 'Aktualisieren' : 'Hinzufügen'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Categories Liste */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {categories.map(category => (
              <div key={category.id} style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                borderRadius: '16px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                border: '1px solid #e5e7eb',
                padding: '20px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                }}>
                  <div style={{ flex: '1' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '8px'
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: '#1F2937',
                        margin: '0'
                      }}>{category.name}</h3>
                      <span style={{
                        background: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)',
                        color: '#374151',
                        fontSize: '12px',
                        fontWeight: '600',
                        padding: '4px 8px',
                        borderRadius: '8px',
                        border: '1px solid #9ca3af'
                      }}>#{category.order_index}</span>
                      <span style={{
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        color: '#92400e',
                        fontSize: '12px',
                        fontWeight: '600',
                        padding: '4px 8px',
                        borderRadius: '8px',
                        border: '1px solid #d97706'
                      }}>
                        {equipment.filter(e => e.category === category.name).length} Items
                      </span>
                    </div>
                    {category.description && (
                      <p style={{
                        fontSize: '14px',
                        color: '#6B7280',
                        margin: '0'
                      }}>{category.description}</p>
                    )}
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '8px'
                  }}>
                    <button
                      onClick={() => handleEditCategory(category)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '8px',
                        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: '#374151',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id, category.name)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '8px',
                        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: '#dc2626',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {categories.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '64px 32px',
              color: '#6B7280'
            }}>
              <Tag size={64} style={{ margin: '0 auto 16px', color: '#d1d5db' }} />
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1F2937',
                marginBottom: '8px'
              }}>Keine Kategorien gefunden</h3>
              <p style={{
                color: '#6B7280',
                fontSize: '16px',
                margin: '0'
              }}>Fügen Sie die erste Kategorie hinzu.</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
